// src/pages/FriendProfile.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import SidebarNavbar from '../components/SidebarNavbar';
import BottomNavbar from '../components/BottomNavbar';
import notify from '../lib/notify';
import { Button as FcButton, Card as FcCard } from '../ui';

export default function FriendProfile({ session, userRole }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [friendData, setFriendData] = useState(null);
  const [areFriends, setAreFriends] = useState(false);
  const [activeCompetitions, setActiveCompetitions] = useState([]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    if (session?.user?.id && userId) {
      checkFriendshipAndFetchData();
    }
  }, [session?.user?.id, userId]);

  const checkFriendshipAndFetchData = async () => {
    setLoading(true);
    try {
      // Check if users are friends
      const minId = session.user.id < userId ? session.user.id : userId;
      const maxId = session.user.id < userId ? userId : session.user.id;

      const { data: friendship, error: friendshipError } = await supabase
        .from('friendships')
        .select('id')
        .eq('user1_id', minId)
        .eq('user2_id', maxId)
        .single();

      if (friendshipError || !friendship) {
        notify('You must be friends to view this profile', 'error');
        navigate('/squad');
        return;
      }

      setAreFriends(true);

      // Fetch friend's profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username, favorite_team')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Fetch friend's referral code
      const { data: referralData } = await supabase
        .from('user_referral_codes')
        .select('referral_code')
        .eq('user_id', userId)
        .single();

      // Fetch competition stats
      const { count: totalCompetitions } = await supabase
        .from('user_selections')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      const { count: wins } = await supabase
        .from('user_selections')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_winner', true);

      // Fetch active competitions
      const { data: activeCompsData, error: activeCompsError } = await supabase
        .from('user_selections')
        .select(`
          id,
          competition_id,
          status,
          competitions (
            id,
            name,
            type,
            league
          )
        `)
        .eq('user_id', userId)
        .in('status', ['active', 'pending'])
        .order('created_at', { ascending: false });

      if (activeCompsError) throw activeCompsError;

      setActiveCompetitions(activeCompsData || []);

      setFriendData({
        username: profileData.username,
        favoriteTeam: profileData.favorite_team,
        friendId: referralData?.referral_code || 'N/A',
        totalCompetitions: totalCompetitions || 0,
        wins: wins || 0,
        winRate: totalCompetitions > 0 ? Math.round((wins / totalCompetitions) * 100) : 0,
        activeCount: activeCompsData?.length || 0
      });
    } catch (error) {
      notify('Failed to load friend profile', 'error');
      navigate('/squad');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <div className="block md:hidden w-full bg-ui-green py-3 px-4">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#284239]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!friendData || !areFriends) {
    return null;
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      <div className="block md:hidden w-full bg-ui-green py-3 px-4">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-28 md:pb-8 fc-workspace">
        {/* Back Button */}
        <FcButton variant="tertiary" type="submit"
          onClick={() => navigate('/squad')}
          className="mb-4 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Squad
        </FcButton>

        {/* Profile Header */}
        <div className="bg-gradient-to-br from-[#284239] to-[#1f332a] rounded-lg shadow-lg p-6 md:p-8 text-white mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <FcCard as="div" padding="none" className="w-24 h-24 bg-surface-card rounded-full flex items-center justify-center">
              <span className="text-5xl text-ui-green font-bold">
                {friendData.username.charAt(0).toUpperCase()}
              </span>
            </FcCard>
            <div className="text-center md:text-left flex-1">
              <h1 className="text-3xl font-bold mb-2">@{friendData.username}</h1>
              <p className="text-white/80 text-sm mb-2">Friend ID: {friendData.friendId}</p>
              {friendData.favoriteTeam && (
                <p className="text-white/90">
                  <span className="text-white/60">Favorite Team:</span> {friendData.favoriteTeam}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-ui-green">{friendData.totalCompetitions}</div>
            <div className="text-sm text-content-secondary mt-1">Total Competitions</div>
          </FcCard>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-ui-success">{friendData.wins}</div>
            <div className="text-sm text-content-secondary mt-1">Wins</div>
          </FcCard>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-action-primary">{friendData.winRate}%</div>
            <div className="text-sm text-content-secondary mt-1">Win Rate</div>
          </FcCard>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-ui-green">{friendData.activeCount}</div>
            <div className="text-sm text-content-secondary mt-1">Active Now</div>
          </FcCard>
        </div>

        {/* Active Competitions */}
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
          <h2 className="text-xl font-bold text-brand-text mb-4">Active Competitions</h2>
          
          {activeCompetitions.length === 0 ? (
            <div className="text-center py-8 text-content-secondary">
              <p>No active competitions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeCompetitions.map((comp) => (
                <div
                  key={comp.id}
                  className="border border-ui-border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-brand-text">{comp.competitions?.name}</h3>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                          {comp.competitions?.type || 'Unknown'}
                        </span>
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                          {comp.competitions?.league || 'Unknown'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          comp.status === 'active' 
                            ? "bg-green-100 text-ui-success"
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {comp.status}
                        </span>
                      </div>
                    </div>
                    <FcButton variant="tertiary" type="submit"
                      onClick={() => navigate(`/competition/${comp.competition_id}`)}
                      className="ml-4"
                    >
                      View →
                    </FcButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FcCard>
      </div>
    </div>
  );
}
